import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Crosshair, Loader2, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { formatRadius } from '../utils/geofence';
import { GEOFENCE_DARK as D } from '../utils/geofenceTheme';

const DEFAULT_CENTER = [78.9629, 20.5937]; // [lng, lat]
export const MIN_RADIUS = 50;
export const MAX_RADIUS = 10000;

function waitForMapplsSdk(maxMs = 20000) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (window.mappls?.Map) {
        resolve(window.mappls);
        return;
      }
      if (Date.now() - started >= maxMs) {
        reject(new Error('Mappls SDK not ready'));
        return;
      }
      window.setTimeout(tick, 150);
    };
    tick();
  });
}

function toMapplsCenter(lat, lng) {
  return [lng, lat];
}

function isMapplsMapReady(map) {
  if (!map) return false;
  if (typeof map.getCanvasContainer === 'function') return true;
  if (typeof map.isStyleLoaded === 'function' && map.isStyleLoaded()) return true;
  return false;
}

function removeOverlay(overlay) {
  if (!overlay) return;
  try {
    overlay.remove?.();
  } catch {
    /* ignore */
  }
  try {
    overlay.setMap?.(null);
  } catch {
    /* ignore */
  }
}

class GeofencePickerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    /* Prevent Mappls SDK errors from crashing the page */
  }

  handleRetry = () => {
    this.setState({ hasError: false, retryKey: this.state.retryKey + 1 });
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback(this.handleRetry);
    }
    const { fallback, ...pickerProps } = this.props;
    return <GeofenceMapPreviewInner key={this.state.retryKey} {...pickerProps} />;
  }
}

function GeofenceMapPreviewInner({
  latitude,
  longitude,
  radiusMeters = 500,
  onChange,
  className,
  mapFocusKey = 0,
}) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const mapLoadedRef = useRef(false);
  const placedKeyRef = useRef('');
  const bootGenerationRef = useRef(0);
  const latitudeRef = useRef(latitude);
  const longitudeRef = useRef(longitude);
  const radiusRef = useRef(radiusMeters);
  const onChangeRef = useRef(onChange);
  const mapContainerId = useId().replace(/:/g, '');
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');

  latitudeRef.current = latitude;
  longitudeRef.current = longitude;
  radiusRef.current = radiusMeters;
  onChangeRef.current = onChange;

  const latNum = Number.parseFloat(latitude);
  const lngNum = Number.parseFloat(longitude);
  const radiusNum = Number.parseFloat(radiusMeters);
  const hasCoords = Number.isFinite(latNum) && Number.isFinite(lngNum);
  const safeRadius =
    Number.isFinite(radiusNum) && radiusNum >= MIN_RADIUS
      ? Math.min(MAX_RADIUS, radiusNum)
      : 500;

  const readCenter = useCallback(() => {
    const lat = Number.parseFloat(latitudeRef.current);
    const lng = Number.parseFloat(longitudeRef.current);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng, hasCoords: true };
    }
    return { lat: DEFAULT_CENTER[1], lng: DEFAULT_CENTER[0], hasCoords: false };
  }, []);

  const emitChange = useCallback((lat, lng, radius) => {
    const latFixed = Number(Number(lat).toFixed(6));
    const lngFixed = Number(Number(lng).toFixed(6));
    const nextRadius = Math.max(
      MIN_RADIUS,
      Math.min(MAX_RADIUS, Math.round(Number(radius) || 500))
    );
    onChangeRef.current?.({
      latitude: String(latFixed),
      longitude: String(lngFixed),
      radiusMeters: nextRadius,
    });
  }, []);

  const clearOverlays = useCallback(() => {
    removeOverlay(markerRef.current);
    removeOverlay(circleRef.current);
    markerRef.current = null;
    circleRef.current = null;
    mapLoadedRef.current = false;
    placedKeyRef.current = '';
  }, []);

  const destroyMapInstance = useCallback(() => {
    clearOverlays();
    const map = mapInstanceRef.current;
    mapInstanceRef.current = null;
    if (map) {
      try {
        map.remove?.();
      } catch {
        /* ignore */
      }
    }
  }, [clearOverlays]);

  const drawZone = useCallback(
    (markerLat, markerLng, radius) => {
      const map = mapInstanceRef.current;
      if (!map || !mapLoadedRef.current || !isMapplsMapReady(map)) return;
      if (!window.mappls) return;

      removeOverlay(markerRef.current);
      removeOverlay(circleRef.current);

      markerRef.current = new window.mappls.Marker({
        map,
        position: { lat: markerLat, lng: markerLng },
        draggable: true,
        width: 36,
        height: 36,
      });

      markerRef.current.on?.('dragend', () => {
        const pos = markerRef.current.getPosition?.() || markerRef.current.getLatLng?.();
        const nextLat = pos?.lat ?? pos?.latitude ?? markerLat;
        const nextLng = pos?.lng ?? pos?.longitude ?? markerLng;
        drawZone(nextLat, nextLng, radiusRef.current);
        emitChange(nextLat, nextLng, radiusRef.current);
      });

      if (typeof window.mappls.Circle === 'function') {
        circleRef.current = new window.mappls.Circle({
          map,
          center: { lat: markerLat, lng: markerLng },
          radius: Number(radius) || 500,
          strokeColor: D.circleBorder,
          strokeOpacity: 1,
          strokeWeight: 2,
          strokeDasharray: [8, 6],
          fillColor: '#FFFFFF',
          fillOpacity: 0.1,
        });
      }

      placedKeyRef.current = `${markerLat},${markerLng},${radius}`;
      try {
        map.setCenter?.(toMapplsCenter(markerLat, markerLng));
      } catch {
        /* ignore */
      }
    },
    [emitChange]
  );

  const createMap = useCallback(async () => {
    const generation = ++bootGenerationRef.current;
    destroyMapInstance();
    setStatus('loading');
    setErrorMessage('');

    await waitForMapplsSdk();
    if (generation !== bootGenerationRef.current) return;

    const el = mapContainerRef.current;
    if (!el) throw new Error('Map container missing');

    const { width, height } = el.getBoundingClientRect();
    if (width < 8 || height < 8) throw new Error('Map container is not visible');

    const { lat, lng, hasCoords: hasInitialCoords } = readCenter();
    const mapCenter = hasInitialCoords ? toMapplsCenter(lat, lng) : DEFAULT_CENTER;
    const zoom = hasInitialCoords ? (safeRadius > 2000 ? 12 : 14) : 5;

    const map = new window.mappls.Map(el, {
      center: mapCenter,
      zoom,
      zoomControl: false,
      location: false,
      scrollWheel: true,
      draggable: true,
      // Prefer dark basemap when the SDK supports it
      theme: 'dark',
      style: 'standard_night',
    });

    if (generation !== bootGenerationRef.current) {
      try {
        map.remove?.();
      } catch {
        /* ignore */
      }
      return;
    }

    mapInstanceRef.current = map;

    await new Promise((resolve, reject) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        if (!isMapplsMapReady(map)) return;
        settled = true;
        resolve();
      };

      if (typeof map.on === 'function') map.on('load', finish);

      const started = Date.now();
      const poll = () => {
        if (settled) return;
        if (isMapplsMapReady(map)) {
          finish();
          return;
        }
        if (Date.now() - started >= 12000) {
          settled = true;
          reject(new Error('Mappls map did not finish loading'));
          return;
        }
        window.requestAnimationFrame(poll);
      };
      poll();
    });

    if (generation !== bootGenerationRef.current) return;

    mapLoadedRef.current = true;
    map.resize?.();

    if (hasInitialCoords) {
      drawZone(lat, lng, radiusRef.current || 500);
    }

    map.on?.('click', (event) => {
      const clickLat = event?.latlng?.lat ?? event?.lngLat?.lat ?? event?.lat;
      const clickLng = event?.latlng?.lng ?? event?.lngLat?.lng ?? event?.lng;
      if (!Number.isFinite(clickLat) || !Number.isFinite(clickLng)) return;
      drawZone(clickLat, clickLng, radiusRef.current || 500);
      emitChange(clickLat, clickLng, radiusRef.current || 500);
    });

    setStatus('ready');
  }, [destroyMapInstance, drawZone, emitChange, readCenter, safeRadius]);

  useEffect(() => {
    let cancelled = false;
    let raf = 0;

    const tryCreate = () => {
      if (cancelled || mapInstanceRef.current) return;
      const el = mapContainerRef.current;
      if (!el) {
        raf = window.requestAnimationFrame(tryCreate);
        return;
      }
      const { width, height } = el.getBoundingClientRect();
      if (width < 8 || height < 8) {
        raf = window.requestAnimationFrame(tryCreate);
        return;
      }
      createMap().catch((error) => {
        if (cancelled) return;
        destroyMapInstance();
        setStatus('error');
        setErrorMessage(error?.message || 'Mappls map failed to load');
      });
    };

    raf = window.requestAnimationFrame(tryCreate);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      bootGenerationRef.current += 1;
      destroyMapInstance();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== 'ready' || !hasCoords || !mapLoadedRef.current || !mapInstanceRef.current) {
      return;
    }
    const key = `${latNum},${lngNum},${safeRadius}`;
    if (placedKeyRef.current === key) return;
    drawZone(latNum, lngNum, safeRadius);
  }, [latitude, longitude, radiusMeters, status, hasCoords, latNum, lngNum, safeRadius, drawZone]);

  useEffect(() => {
    if (!mapFocusKey || status !== 'ready' || !mapInstanceRef.current) return;
    const { lat, lng, hasCoords: hasCenter } = readCenter();
    if (!hasCenter) return;
    try {
      mapInstanceRef.current.setCenter?.(toMapplsCenter(lat, lng));
      mapInstanceRef.current.setZoom?.(safeRadius > 2000 ? 12 : 14);
      mapInstanceRef.current.resize?.();
    } catch {
      /* ignore */
    }
  }, [mapFocusKey, status, readCenter, safeRadius]);

  const handleRetry = () => {
    bootGenerationRef.current += 1;
    destroyMapInstance();
    createMap().catch((error) => {
      destroyMapInstance();
      setStatus('error');
      setErrorMessage(error?.message || 'Mappls map failed to load');
    });
  };

  const recenter = () => {
    const { lat, lng, hasCoords: hasCenter } = readCenter();
    if (!hasCenter || !mapInstanceRef.current) return;
    try {
      mapInstanceRef.current.setCenter?.(toMapplsCenter(lat, lng));
      mapInstanceRef.current.setZoom?.(safeRadius > 2000 ? 12 : 14);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={cn('relative', className)}>
      <div
        className="relative overflow-hidden rounded-[20px]"
        style={{
          backgroundColor: D.mapBg,
          boxShadow: D.shadow,
        }}
      >
        {status === 'loading' && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            style={{ backgroundColor: `${D.mapBg}E6` }}
          >
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: D.secondaryText }} />
          </div>
        )}
        {status === 'error' && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-4 text-center text-sm"
            style={{ backgroundColor: `${D.mapBg}F2`, color: D.secondaryText }}
          >
            <p>Map failed to load. Tap retry or set location from GPS.</p>
            {errorMessage ? (
              <p className="text-xs" style={{ color: D.placeholder }}>
                {errorMessage}
              </p>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRetry}
              style={{
                backgroundColor: D.inputBg,
                borderColor: D.inputBorder,
                color: D.whiteText,
              }}
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Retry map
            </Button>
          </div>
        )}

        <div className="h-[220px] w-full sm:h-[280px]">
          <div id={mapContainerId} ref={mapContainerRef} className="h-full w-full" />
        </div>

        {hasCoords ? (
          <div
            className="pointer-events-none absolute left-1/2 top-[42%] z-[5] -translate-x-1/2 rounded-md px-2 py-0.5 text-[11px] font-medium backdrop-blur-sm"
            style={{
              backgroundColor: 'rgba(11,11,13,0.7)',
              color: D.primaryText,
            }}
          >
            {formatRadius(safeRadius)}
          </div>
        ) : null}

        <button
          type="button"
          onClick={recenter}
          className="absolute bottom-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border shadow-lg backdrop-blur-sm"
          style={{
            backgroundColor: D.iconBg,
            borderColor: D.inputBorder,
            color: D.primaryText,
            boxShadow: D.shadow,
          }}
          aria-label="Recenter map"
        >
          <Crosshair className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function GeofenceCirclePicker(props) {
  return (
    <GeofencePickerErrorBoundary
      {...props}
      fallback={(onRetry) => (
        <div
          className="flex h-[220px] flex-col items-center justify-center gap-3 rounded-2xl border px-4 text-center text-sm sm:h-[280px]"
          style={{
            backgroundColor: D.mapBg,
            borderColor: D.inputBorder,
            color: D.secondaryText,
          }}
        >
          <p>Map could not be displayed.</p>
          {onRetry ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              style={{
                backgroundColor: D.inputBg,
                borderColor: D.inputBorder,
                color: D.whiteText,
              }}
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Retry map
            </Button>
          ) : null}
        </div>
      )}
    />
  );
}
